import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SalesPointPerformanceMetrics } from '../types/analytics.types.js';
import type { ProductPerformance } from '../types/analytics.types.js';
import { SalesPointTag } from '../../transactions/components/SalesPointTag/SalesPointTag.js';
import { buildSalesPointColorIndexMap } from '../../transactions/domain/salesPointTagColors.js';

interface Props {
  salesPoints: SalesPointPerformanceMetrics[];
  products: ProductPerformance[];
  isLoading?: boolean;
}

export function ProductSalesPointCharts({ salesPoints, products, isLoading = false }: Props) {
  const { t } = useTranslation('admin');
  const currencySuffix = t('shared.currencySuffix');

  const topProducts = products
    .filter((p) => (p.quantity ?? 0) > 0 || (p.revenue ?? 0) > 0)
    .sort((a, b) => (b.revenue ?? 0) - (a.revenue ?? 0))
    .slice(0, 10);

  const topSalesPoints = salesPoints
    .filter((sp) => (sp.orders ?? 0) > 0 || (sp.revenue ?? 0) > 0)
    .sort((a, b) => (b.revenue ?? 0) - (a.revenue ?? 0))
    .slice(0, 10);

  const salesPointColorIndexMap = useMemo(
    () => buildSalesPointColorIndexMap(topSalesPoints.map((sp) => sp.salesPointId)),
    [topSalesPoints],
  );

  const maxRevenueProduct =
    topProducts.length > 0 ? Math.max(...topProducts.map((p) => Math.max(p.revenue, 1)), 1) : 1;
  const maxRevenueSalesPoint =
    topSalesPoints.length > 0
      ? Math.max(...topSalesPoints.map((sp) => Math.max(sp.revenue, 1)), 1)
      : 1;

  return (
    <div
      className="grid grid-cols-[repeat(auto-fit,minmax(340px,1fr))] gap-5 max-md:grid-cols-1"
      data-testid="product-sales-point-charts-grid"
    >
      <div className="flex flex-col gap-3">
        <div className="mb-1">
          <h4 className="m-0 mb-1 text-[15px] font-bold text-[var(--color-an-text)]">
            {t('analytics.productKioskCharts.topProductsTitle')}
          </h4>
          <span className="block text-xs text-[var(--color-an-text-muted)]">
            {t('analytics.productKioskCharts.topProductsSubtitle')}
          </span>
        </div>
        {(() => {
          if (isLoading && topProducts.length === 0) {
            return (
              <div className="py-8 text-center text-sm text-[var(--color-an-text-muted)]">
                <div className="an-loading-spinner" />
                <p>{t('analytics.productKioskCharts.loadingProducts')}</p>
              </div>
            );
          }
          if (topProducts.length === 0) {
            return (
              <div className="py-8 text-center text-sm text-[var(--color-an-text-muted)]">
                {t('analytics.productKioskCharts.noData')}
              </div>
            );
          }
          return (
            <div className="flex flex-col gap-2">
              {topProducts.map((p, idx) => {
                const revenue = p.revenue ?? 0;
                const quantity = p.quantity ?? 0;
                const width = maxRevenueProduct > 0 ? (revenue / maxRevenueProduct) * 100 : 0;

                const productName =
                  p.name && p.name.trim()
                    ? p.name
                    : t('analytics.productKioskCharts.productFallback', { id: p.productId });

                return (
                  <div
                    className="grid grid-cols-[28px_1fr_100px_70px] max-md:grid-cols-[28px_1fr_60px] items-center gap-2.5 rounded-[10px] border border-[var(--color-an-border)] bg-[var(--color-an-surface-muted,var(--color-an-bg-elevated))] p-2.5 transition-all hover:translate-x-0.5 hover:border-[var(--color-an-primary)] hover:shadow-sm"
                    key={p.productId}
                  >
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[var(--color-an-primary-soft)] text-xs font-bold text-[var(--color-an-primary)]">
                      {idx + 1}
                    </div>
                    <div className="min-w-0">
                      <div
                        className="mb-1 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] font-semibold text-[var(--color-an-text)]"
                        title={productName}
                      >
                        {productName}
                      </div>
                      <div className="flex flex-wrap gap-1.5 text-[11px] text-[var(--color-an-text-muted)]">
                        {quantity > 0 ? (
                          <span className="whitespace-nowrap rounded border border-[var(--color-an-border)] bg-[var(--color-an-surface)] px-1.5 py-0.5">
                            {t('analytics.productKioskCharts.units', { count: quantity })}
                          </span>
                        ) : null}
                        {p.orders > 0 ? (
                          <span className="whitespace-nowrap rounded border border-[var(--color-an-border)] bg-[var(--color-an-surface)] px-1.5 py-0.5">
                            {t('analytics.productKioskCharts.ordersShort', { count: p.orders })}
                          </span>
                        ) : null}
                        {p.refunds > 0 ? (
                          <span className="whitespace-nowrap rounded border border-red-200 bg-red-50 px-1.5 py-0.5 font-semibold text-red-600">
                            {t('analytics.productKioskCharts.refundsShort', { count: p.refunds })}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {width > 0 ? (
                      <>
                        <div className="h-5 overflow-hidden rounded-md bg-[var(--color-an-border)] max-md:hidden">
                          <div
                            className="h-full rounded-md bg-gradient-to-r from-[var(--color-an-chart-1)] to-[var(--color-an-chart-6)] transition-[width]"
                            style={{ width: `${width}%` }}
                            title={t('analytics.productKioskCharts.barTitle', {
                              name: productName,
                              amount: revenue.toFixed(0),
                              currency: currencySuffix,
                            })}
                          />
                        </div>
                        <div className="whitespace-nowrap text-right text-[13px] font-bold text-[var(--color-an-chart-2)] max-md:text-left">
                          {revenue.toFixed(0)} {currencySuffix}
                        </div>
                      </>
                    ) : (
                      <div className="col-span-2 py-1 text-center text-[11px] italic text-[var(--color-an-text-muted)]">
                        {t('analytics.productKioskCharts.noRevenue')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      <div className="flex flex-col gap-3">
        <div className="mb-1">
          <h4 className="m-0 mb-1 text-[15px] font-bold text-[var(--color-an-text)]">
            {t('analytics.productKioskCharts.topKiosksTitle')}
          </h4>
          <span className="block text-xs text-[var(--color-an-text-muted)]">
            {t('analytics.productKioskCharts.topKiosksSubtitle')}
          </span>
        </div>
        {(() => {
          if (isLoading && topSalesPoints.length === 0) {
            return (
              <div className="py-8 text-center text-sm text-[var(--color-an-text-muted)]">
                <div className="an-loading-spinner" />
                <p>{t('analytics.productKioskCharts.loadingKiosks')}</p>
              </div>
            );
          }
          if (topSalesPoints.length === 0) {
            return (
              <div className="py-8 text-center text-sm text-[var(--color-an-text-muted)]">
                {t('analytics.productKioskCharts.noData')}
              </div>
            );
          }
          return (
            <div className="flex flex-col gap-2">
              {topSalesPoints.map((sp, idx) => {
                const revenue = sp.revenue ?? 0;
                const width = maxRevenueSalesPoint > 0 ? (revenue / maxRevenueSalesPoint) * 100 : 0;
                const salesPointLabel =
                  sp.salesPointName ??
                  t('analytics.productKioskCharts.kioskFallback', { id: sp.salesPointId });

                return (
                  <div
                    className="grid grid-cols-[28px_1fr_100px_70px] max-md:grid-cols-[28px_1fr_60px] items-center gap-2.5 rounded-[10px] border border-[var(--color-an-border)] bg-[var(--color-an-surface-muted,var(--color-an-bg-elevated))] p-2.5 transition-all hover:translate-x-0.5 hover:border-[var(--color-an-primary)] hover:shadow-sm"
                    key={sp.salesPointId}
                  >
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[var(--color-an-primary-soft)] text-xs font-bold text-[var(--color-an-primary)]">
                      {idx + 1}
                    </div>
                    <div className="min-w-0">
                      <div
                        className="mb-1 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] font-semibold text-[var(--color-an-text)]"
                        title={salesPointLabel}
                      >
                        <SalesPointTag
                          paletteIndex={
                            salesPointColorIndexMap.get(sp.salesPointId) ?? sp.salesPointId
                          }
                        >
                          {salesPointLabel}
                        </SalesPointTag>
                      </div>
                      <div className="flex flex-wrap gap-1.5 text-[11px] text-[var(--color-an-text-muted)]">
                        {sp.orders > 0 ? (
                          <span className="whitespace-nowrap rounded border border-[var(--color-an-border)] bg-[var(--color-an-surface)] px-1.5 py-0.5">
                            {t('analytics.productKioskCharts.ordersShort', { count: sp.orders })}
                          </span>
                        ) : null}
                        {sp.avgOrderValue > 0 ? (
                          <span className="whitespace-nowrap rounded border border-[var(--color-an-border)] bg-[var(--color-an-surface)] px-1.5 py-0.5">
                            {t('analytics.productKioskCharts.avgOrder', {
                              amount: sp.avgOrderValue.toFixed(0),
                              currency: currencySuffix,
                            })}
                          </span>
                        ) : null}
                        {sp.refunds > 0 ? (
                          <span className="whitespace-nowrap rounded border border-red-200 bg-red-50 px-1.5 py-0.5 font-semibold text-red-600">
                            {t('analytics.productKioskCharts.refundsShort', { count: sp.refunds })}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {width > 0 ? (
                      <>
                        <div className="h-5 overflow-hidden rounded-md bg-[var(--color-an-border)] max-md:hidden">
                          <div
                            className="h-full rounded-md bg-gradient-to-r from-[var(--color-an-chart-1)] to-[var(--color-an-chart-5)] transition-[width]"
                            style={{ width: `${width}%` }}
                            title={t('analytics.productKioskCharts.barTitle', {
                              name: salesPointLabel,
                              amount: revenue.toFixed(0),
                              currency: currencySuffix,
                            })}
                          />
                        </div>
                        <div className="whitespace-nowrap text-right text-[13px] font-bold text-[var(--color-an-chart-2)] max-md:text-left">
                          {revenue.toFixed(0)} {currencySuffix}
                        </div>
                      </>
                    ) : (
                      <div className="col-span-2 py-1 text-center text-[11px] italic text-[var(--color-an-text-muted)]">
                        {t('analytics.productKioskCharts.noRevenue')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
